import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Zap, Lock, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try { await login(email.trim(), password); nav("/"); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Login failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="dark flex min-h-screen items-center justify-center bg-[hsl(222_47%_6%)] p-4">
      <div className="pointer-events-none fixed inset-0 opacity-40" style={{ background: "radial-gradient(600px 300px at 20% 10%, hsl(217 91% 60% / 0.18), transparent), radial-gradient(500px 300px at 90% 90%, hsl(160 84% 39% / 0.15), transparent)" }} />
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[hsl(217_33%_14%)] p-8 shadow-2xl">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-emerald-500 font-head text-xl font-black text-white">C</div>
          <div><p className="font-head text-xl font-bold text-white">Connect Tym</p><p className="text-[10px] uppercase tracking-widest text-slate-400">EV Tym Operations CRM</p></div>
        </div>
        <h1 className="font-head text-2xl font-bold text-white">Sign in</h1>
        <p className="mt-1 text-sm text-slate-400">Use the Login ID and password issued by your Super Admin.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label className="text-slate-300">Login ID or Email</Label>
            <div className="relative"><User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input data-testid="login-email" type="text" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9" placeholder="CTM-MGR-001" /></div>
          </div>
          <div>
            <Label className="text-slate-300">Password</Label>
            <div className="relative"><Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9" placeholder="••••••••" /></div>
          </div>
          {err && <p className="rounded-lg bg-rose-500/10 px-3 py-2 text-sm text-rose-400" data-testid="login-error">{err}</p>}
          <Button type="submit" disabled={busy} data-testid="login-submit" className="h-11 w-full text-base font-semibold active:scale-[0.98]">
            <Zap className="mr-1.5 h-4 w-4" />{busy ? "Signing in…" : "Login to Connect Tym CRM"}
          </Button>
          <div className="text-center"><Link to="/forgot-password" className="text-sm text-blue-400 hover:underline" data-testid="forgot-password-link">Super Admin: forgot password?</Link><p className="mt-1 text-[11px] text-slate-500">Staff passwords can only be reset by the Super Admin.</p></div>
        </form>
      </div>
    </div>
  );
}
